import { useState, useCallback, useMemo } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, IconButton,
  FormControlLabel, Switch,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon, ArrowUpward as ArrowUpIcon, ArrowDownward as ArrowDownIcon } from '@mui/icons-material';
import { AdminListPanel } from './AdminListPanel';
import { api } from '../../api/rpc';
import { gwtStyles } from '../../config/theme';
import type { Rule } from '../../api/types';
import { pushError, pushSuccess } from '../../stores/notificationStore';

const baseColumns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'type', headerName: 'Type', width: 100 },
  { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 200 },
  { field: 'priority', headerName: 'Priority', width: 70 },
  {
    field: 'enabled',
    headerName: 'Enabled',
    width: 70,
    renderCell: (params) => (params.value ? '✓' : '✗'),
  },
  {
    field: 'reorder',
    headerName: '↕',
    width: 60,
    sortable: false,
    renderCell: () => null, // placeholder — populated dynamically
  },
  {
    field: 'actions',
    headerName: '',
    width: 70,
    sortable: false,
    renderCell: () => null,
  },
];

/**
 * Rules admin tab — RuleListPanel equivalent.
 * Lists routing/auto-assign/notification rules.
 */
export const RulesPanel = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editRule, setEditRule] = useState<Rule | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Rule | null>(null);

  const { data, isLoading } = useQuery<Rule[]>({
    queryKey: ['rules'],
    queryFn: async () => {
      try {
        const result = await api.getRules();
        return result.records ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
  });

  const rules = useMemo(() => {
    const all = data ?? [];
    if (!search) return all;
    const s = search.toLowerCase();
    return all.filter(
      (r) =>
        r.name.toLowerCase().includes(s) ||
        r.type.toLowerCase().includes(s) ||
        (r.description ?? '').toLowerCase().includes(s),
    );
  }, [data, search]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['rules'] });
  }, [queryClient]);

  const handleAdd = useCallback(() => {
    setIsNew(true);
    setEditRule({ id: '', name: '', type: '', enabled: true, condition: '', action: '' });
  }, []);

  const handleDelete = useCallback(async (rule: Rule) => {
    try {
      await api.deleteRule(rule.id);
      pushSuccess(`Rule "${rule.name}" deleted`);
      refresh();
    } catch (e) {
      pushError(`Failed to delete rule: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setConfirmDelete(null);
    }
  }, [refresh]);

  /** Swap priority with adjacent rule */
  const handleReorder = useCallback(async (rule: Rule, direction: 'up' | 'down') => {
    const sorted = [...rules].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
    const idx = sorted.findIndex((r) => r.id === rule.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    const myPriority = rule.priority ?? idx;
    const otherPriority = other.priority ?? swapIdx;
    try {
      await Promise.all([
        api.updateRule({ objKey: rule.id, name: rule.name, trigger: rule.type, condition: rule.condition ?? '', action: rule.action ?? '', active: rule.enabled, priority: otherPriority }),
        api.updateRule({ objKey: other.id, name: other.name, trigger: other.type, condition: other.condition ?? '', action: other.action ?? '', active: other.enabled, priority: myPriority }),
      ]);
      refresh();
    } catch (e) {
      pushError(`Failed to reorder: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [rules, refresh]);

  return (
    <>
      <AdminListPanel
        title="Rules"
        search={search}
        onSearchChange={setSearch}
        onRefresh={refresh}
        onAdd={handleAdd}
        statusText={`${rules.length} rules`}
      >
        <DataGrid
          rows={rules}
          columns={baseColumns.map((col) =>
            col.field === 'actions'
              ? {
                  ...col,
                  renderCell: (params) => (
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton size="small" onClick={() => { setIsNew(false); setEditRule(params.row as Rule); }} sx={{ width: 24, height: 24 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => setConfirmDelete(params.row as Rule)} sx={{ width: 24, height: 24 }}>
                        <DeleteIcon sx={{ fontSize: 14, color: 'error.main' }} />
                      </IconButton>
                    </Box>
                  ),
                }
              : col.field === 'reorder'
              ? {
                  ...col,
                  renderCell: (params) => {
                    const sorted = [...rules].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
                    const idx = sorted.findIndex((r) => r.id === (params.row as Rule).id);
                    return (
                      <Box sx={{ display: 'flex', gap: 0 }}>
                        <IconButton
                          size="small"
                          disabled={idx <= 0}
                          onClick={() => handleReorder(params.row as Rule, 'up')}
                          sx={{ width: 20, height: 20, p: 0 }}
                        >
                          <ArrowUpIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={idx >= sorted.length - 1}
                          onClick={() => handleReorder(params.row as Rule, 'down')}
                          sx={{ width: 20, height: 20, p: 0 }}
                        >
                          <ArrowDownIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    );
                  },
                }
              : col,
          )}
          getRowId={(row) => (row as Rule).id}
          loading={isLoading}
          density="compact"
          disableColumnMenu
          rowHeight={24}
          columnHeaderHeight={28}
          sx={{
            border: 'none',
            fontSize: 12,
            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: gwtStyles.gridRowOdd },
            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: gwtStyles.gridRowEven },
          }}
        />
      </AdminListPanel>

      {editRule && (
        <RuleDialog
          rule={editRule}
          isNew={isNew}
          open
          onClose={() => setEditRule(null)}
          onSaved={() => { setEditRule(null); refresh(); }}
        />
      )}

      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: 14 }}>Delete Rule</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              Are you sure you want to delete rule &quot;{confirmDelete.name}&quot;?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button size="small" onClick={() => setConfirmDelete(null)} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
            <Button size="small" variant="contained" color="error" onClick={() => handleDelete(confirmDelete)} sx={{ textTransform: 'none', fontSize: 12 }}>Delete</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

/* ── RuleDialog ── */
const RuleDialog = ({ rule, isNew, open, onClose, onSaved }: {
  rule: Rule; isNew: boolean; open: boolean; onClose: () => void; onSaved: () => void;
}) => {
  const [name, setName] = useState(rule.name);
  const [type, setType] = useState(rule.type);
  const [condition, setCondition] = useState(rule.condition ?? '');
  const [action, setAction] = useState(rule.action ?? '');
  const [enabled, setEnabled] = useState(rule.enabled);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateRule({
        ...(isNew ? {} : { objKey: rule.id }),
        name,
        trigger: type,
        condition,
        action,
        active: enabled,
      });
      pushSuccess(`Rule "${name}" ${isNew ? 'created' : 'updated'}`);
      onSaved();
    } catch (e) {
      pushError(`Failed to save rule: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 500,
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>
        {isNew ? 'Add Rule' : 'Edit Rule'}
        <IconButton size="small" onClick={onClose} sx={{ width: 24, height: 24 }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ py: 2 }}>
        <FieldRow label="Name">
          <TextField size="small" fullWidth value={name} onChange={(e) => setName(e.target.value)} sx={fsx} />
        </FieldRow>
        <FieldRow label="Trigger">
          <TextField size="small" fullWidth value={type} onChange={(e) => setType(e.target.value)} sx={fsx} />
        </FieldRow>
        <FieldRow label="Enabled">
          <FormControlLabel
            control={<Switch size="small" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label=""
            sx={{ ml: 0 }}
          />
        </FieldRow>
        <FieldRow label="Condition">
          <TextField size="small" fullWidth multiline rows={3} value={condition} onChange={(e) => setCondition(e.target.value)} sx={fsx} />
        </FieldRow>
        <FieldRow label="Action">
          <TextField size="small" fullWidth multiline rows={2} value={action} onChange={(e) => setAction(e.target.value)} sx={fsx} />
        </FieldRow>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>Cancel</Button>
        <Button
          size="small"
          variant="contained"
          disabled={saving || !name || !type || !condition || !action}
          onClick={handleSave}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FieldRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75, gap: 1 }}>
    <Typography variant="body2" sx={{ fontSize: 12, minWidth: 90, textAlign: 'right', flexShrink: 0, pt: 0.5 }}>{label}:</Typography>
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Box>
);

const fsx = { '& .MuiInputBase-root': { fontSize: 12 } };
